/**
 * @fileoverview Hero Platform Diagram
 *
 * Purpose: The homepage hero — an abstract, brand-led motion graphic built to
 * the user's Figma guide. A vertical pipeline that communicates what Morgan
 * does without revealing any real product UI:
 *
 *   Matter inputs (any form)  →  Morgan intelligence engine  →  Outputs
 *        with a "self-updating loop" (left) and "practitioner review" (right).
 *
 * The five boxes the guide arrows point to carry stylised, looping feature
 * animations (the explicit ask):
 *   - Engine working box      — cycles through the engine's capabilities,
 *                               syncing with the highlighted task list.
 *   - Day Ones                — a document drafting itself.
 *   - Reporting               — a live chart building.
 *   - Forms                   — form fields populating + ticking.
 *   - Creditor correspondence — a letter typing + sending.
 *
 * Motion is smooth / eased / continuously looping to match the polished feel of
 * modern AI-ops hero animations. Brand CSS variables are pinned per theme (via
 * useIsDark) so it renders a dedicated light or dark variant — dark surfaces use
 * bg-white dark:bg-card with a lifted brand green for legibility. Respects
 * prefers-reduced-motion.
 *
 * Used by: DemoSection (-sections.demo.tsx)
 */

import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Check,
  FileSpreadsheet,
  FileText,
  Flag,
  Mail,
  Send,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/misc";

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

const INPUTS: Array<{ label: string; icon: LucideIcon; color: string }> = [
  { label: "Docs", icon: FileText, color: "text-blue-600" },
  { label: "Excel", icon: FileSpreadsheet, color: "text-green-600" },
  { label: "PDFs", icon: FileText, color: "text-red-600" },
  { label: "Emails", icon: Mail, color: "text-blue-500" },
];

const INTEGRATION_ICONS: Array<{ src: string; alt: string }> = [
  { src: "/logos/xero.svg", alt: "Xero" },
  { src: "/logos/sharepoint.svg", alt: "SharePoint" },
  { src: "/logos/onedrive.svg", alt: "OneDrive" },
  { src: "/logos/outlook.svg", alt: "Outlook" },
  { src: "/logos/infotrack.png", alt: "InfoTrack" },
];

// ---------------------------------------------------------------------------
// Engine capabilities (left list ↔ right viz, cycled)
// ---------------------------------------------------------------------------

const ENGINE_TASKS = [
  "Extract assets & liabilities",
  "Generate creditor list & match POD",
  "Auto-calculate deadlines & review windows",
  "Estimate date of insolvency",
  "Investigate voidables, civil & criminal claims",
] as const;

const TASK_MS = 2200;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface HeroPlatformDiagramProps {
  isActive?: boolean;
}

export function HeroPlatformDiagram({
  isActive = true,
}: HeroPlatformDiagramProps) {
  const reducedMotion = useReducedMotion() ?? false;
  const playing = isActive && !reducedMotion;

  const [task, setTask] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(
      () => setTask((t) => (t + 1) % ENGINE_TASKS.length),
      TASK_MS,
    );
    return () => clearInterval(id);
  }, [playing]);

  // Measure the inputs + engine + outputs panels so the side rails connect from
  // the vertical middle of each, with a pulse travelling the full line.
  // Left rail (self-updating): inputs ↔ engine. Right rail (review): engine ↔ outputs.
  const containerRef = useRef<HTMLDivElement>(null);
  const inputsRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<HTMLDivElement>(null);
  const outputsRef = useRef<HTMLDivElement>(null);
  const rail = useRailGeometry(containerRef, inputsRef, engineRef, outputsRef);
  const isDark = useIsDark();

  return (
    <div
      className="h-full w-full"
      style={
        (isDark
          ? {
              "--background": "0 0% 0%",
              "--foreground": "0 0% 98%",
              "--card": "220 6% 8%",
              "--card-foreground": "0 0% 98%",
              // Brand green lifted to a mid tone so it stays legible as panel
              // fills, accent text and rails on dark surfaces.
              "--primary": "128 36% 44%",
              "--primary-foreground": "0 0% 100%",
            }
          : {
              "--background": "0 0% 100%",
              "--foreground": "140 4% 14%",
              "--card": "50 100% 99%",
              "--card-foreground": "140 4% 14%",
              "--primary": "128 38% 17%",
              "--primary-foreground": "0 0% 100%",
            }) as CSSProperties
      }
    >
      <div
        ref={containerRef}
        className="relative flex h-full w-full overflow-hidden rounded-xl border border-primary/10 bg-white p-3 dark:bg-card min-[1160px]:p-5"
      >
        {/* Left gutter (reserves space for the self-updating rail) */}
        <div className="hidden w-24 shrink-0 min-[1160px]:block" />

        {/* Side rails + labels (measured overlay) */}
        <SideRails rail={rail} playing={playing} />

        {/* Stacked panels */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Inputs */}
          <Panel title="Matter inputs (in any form)" panelRef={inputsRef}>
            <div className="flex w-full flex-wrap items-center justify-center gap-1 min-[1160px]:gap-1.5">
              {INPUTS.map((input) => {
                const Icon = input.icon;
                return (
                  <div
                    key={input.label}
                    className="flex items-center gap-1 rounded-md bg-white px-1.5 py-1 shadow-sm dark:bg-card min-[1160px]:gap-1.5 min-[1160px]:px-2.5 min-[1160px]:py-1.5"
                  >
                    <Icon
                      className={cn(
                        "h-3 w-3 min-[1160px]:h-3.5 min-[1160px]:w-3.5",
                        input.color,
                      )}
                    />
                    <span className="text-[10px] font-medium text-foreground/80 min-[1160px]:text-[11px]">
                      {input.label}
                    </span>
                  </div>
                );
              })}
              <div className="flex items-center gap-1 rounded-md bg-white px-1.5 py-1 shadow-sm dark:bg-card min-[1160px]:gap-1.5 min-[1160px]:px-2.5 min-[1160px]:py-1.5">
                <div className="flex items-center gap-1">
                  {INTEGRATION_ICONS.map((it) => (
                    <img
                      key={it.alt}
                      src={it.src}
                      alt={it.alt}
                      className="h-3 w-3 object-contain min-[1160px]:h-3.5 min-[1160px]:w-3.5"
                    />
                  ))}
                </div>
                <span className="text-[10px] font-medium text-foreground/80 min-[1160px]:text-[11px]">
                  Integrations
                </span>
              </div>
            </div>
          </Panel>

          <Connector />

          {/* Engine */}
          <Panel title="Intelligence engine" grow panelRef={engineRef}>
            <div className="flex min-h-0 flex-1 flex-col gap-2 min-[1160px]:flex-row">
              {/* Capability list (hidden on mobile — viz alone tells the story) */}
              <div className="hidden w-full shrink-0 flex-col justify-start gap-2 rounded-lg bg-white p-2.5 dark:bg-card min-[1160px]:flex min-[1160px]:w-[38%] min-[1160px]:pt-5">
                {ENGINE_TASKS.map((t, i) => {
                  const isActive = i === task;
                  return (
                    <div
                      key={t}
                      className={cn(
                        "flex items-center gap-1.5 rounded px-1.5 py-1.5 transition-colors duration-300",
                        isActive ? "bg-primary/[0.06]" : "bg-transparent",
                      )}
                    >
                      <span
                        className={cn(
                          "h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-300",
                          isActive ? "bg-primary" : "bg-primary/20",
                        )}
                      />
                      <span
                        className={cn(
                          "truncate text-[10px] transition-colors duration-300 min-[1160px]:text-[11px]",
                          isActive
                            ? "font-semibold text-primary"
                            : "text-foreground/55",
                        )}
                      >
                        {t}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Working viz */}
              <div className="relative min-w-0 flex-1 overflow-hidden rounded-lg bg-white p-3 dark:bg-card">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={task}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35 }}
                    className="h-full w-full"
                  >
                    <EngineViz task={task} playing={playing} />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </Panel>

          <Connector />

          {/* Outputs */}
          <Panel title="Outputs" grow panelRef={outputsRef}>
            {/* Mobile: one swipeable output at a time */}
            <div className="flex min-h-0 w-full flex-1 min-[1160px]:hidden">
              <OutputsCarousel playing={playing} />
            </div>
            {/* Desktop: full grid of all four */}
            <div className="hidden min-h-0 w-full flex-1 grid-cols-4 gap-2 min-[1160px]:grid">
              {OUTPUTS.map(({ label, Viz }) => (
                <OutputBox key={label} label={label}>
                  <Viz playing={playing} />
                </OutputBox>
              ))}
            </div>
          </Panel>
        </div>

        {/* Right gutter (reserves space for the practitioner-review rail) */}
        <div className="hidden w-24 shrink-0 min-[1160px]:block" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

function Panel({
  title,
  grow,
  panelRef,
  children,
}: {
  title: string;
  grow?: boolean;
  panelRef?: RefObject<HTMLDivElement>;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col", grow && "min-h-0 flex-1")}>
      <span className="mb-1 pl-0.5 text-[12px] font-medium text-foreground/70">
        {title}
      </span>
      <div
        ref={panelRef}
        className={cn(
          "flex rounded-xl bg-primary p-2.5",
          grow && "min-h-0 flex-1",
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** The four pipeline outputs, shared by the desktop grid + mobile carousel. */
const OUTPUTS: Array<{
  label: string;
  Viz: (props: { playing: boolean }) => ReactNode;
}> = [
  { label: "Day Ones", Viz: DayOnesViz },
  { label: "Forms & lodgements", Viz: FormsViz },
  { label: "Reporting", Viz: ReportingViz },
  { label: "Creditor correspondence", Viz: CorrespondenceViz },
];

/**
 * Mobile-only outputs carousel — shows one output at a time, auto-advancing on a
 * loop and swipeable by drag, with tappable dot indicators.
 */
function OutputsCarousel({ playing }: { playing: boolean }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % OUTPUTS.length), 3200);
    return () => clearInterval(id);
  }, [playing]);

  const go = (dir: number) =>
    setIdx((i) => (i + dir + OUTPUTS.length) % OUTPUTS.length);

  const current = OUTPUTS[idx];
  const Viz = current.Viz;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-2">
      <div className="relative min-h-0 flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            className="absolute inset-0"
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -32 }}
            transition={{ duration: 0.3 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.x < -40) go(1);
              else if (info.offset.x > 40) go(-1);
            }}
          >
            <OutputBox label={current.label}>
              <Viz playing={playing} />
            </OutputBox>
          </motion.div>
        </AnimatePresence>
      </div>
      {/* Dot indicators */}
      <div className="flex shrink-0 items-center justify-center gap-1.5">
        {OUTPUTS.map((o, i) => (
          <button
            key={o.label}
            type="button"
            aria-label={`Show ${o.label}`}
            onClick={() => setIdx(i)}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              i === idx
                ? "w-4 bg-primary-foreground"
                : "w-1.5 bg-primary-foreground/40",
            )}
          />
        ))}
      </div>
    </div>
  );
}

function OutputBox({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-white p-2 dark:bg-card">
      <span className="mb-1 truncate text-[10px] font-semibold text-foreground/70">
        {label}
      </span>
      <div className="relative min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * Static vertical connector between panels (matches the Figma guide).
 * Each panel has a title label below the connector, so the line spans the full
 * gap — from the box above down past the next panel's label, tucking under the
 * next box for a clean join.
 */
function Connector() {
  return (
    <div className="relative h-4">
      <div className="absolute -bottom-6 left-1/2 top-0 w-2 -translate-x-1/2 bg-primary" />
    </div>
  );
}

/**
 * Tracks whether the app is in dark mode by observing the `.dark` class on the
 * document root (toggled by theme-switcher.tsx). Drives the diagram's pinned
 * CSS variable set so it renders a true dark variant rather than the light one.
 */
function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsDark(root.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

interface RailGeometry {
  width: number;
  height: number;
  leftPanelX: number;
  rightPanelX: number;
  inputsMidY: number;
  engineMidY: number;
  outputsMidY: number;
}

/**
 * Measures the container + inputs/engine/outputs panels so the side rails can
 * connect from the exact vertical middle of each panel. Re-measures on resize.
 */
function useRailGeometry(
  containerRef: RefObject<HTMLDivElement>,
  inputsRef: RefObject<HTMLDivElement>,
  engineRef: RefObject<HTMLDivElement>,
  outputsRef: RefObject<HTMLDivElement>,
): RailGeometry | null {
  const [rail, setRail] = useState<RailGeometry | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const inputs = inputsRef.current;
    const engine = engineRef.current;
    const outputs = outputsRef.current;
    if (!container || !inputs || !engine || !outputs) return;

    // Side rails (and their gutters) only exist on the desktop layout, which
    // kicks in at the same 1160px breakpoint used by the CSS utilities below.
    const mql = window.matchMedia("(min-width: 1160px)");

    const measure = () => {
      if (!mql.matches) {
        setRail(null);
        return;
      }
      const c = container.getBoundingClientRect();
      const n = inputs.getBoundingClientRect();
      const e = engine.getBoundingClientRect();
      const o = outputs.getBoundingClientRect();
      setRail({
        width: c.width,
        height: c.height,
        leftPanelX: e.left - c.left,
        rightPanelX: e.right - c.left,
        inputsMidY: n.top - c.top + n.height / 2,
        engineMidY: e.top - c.top + e.height / 2,
        outputsMidY: o.top - c.top + o.height / 2,
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    mql.addEventListener("change", measure);
    return () => {
      ro.disconnect();
      mql.removeEventListener("change", measure);
    };
  }, [containerRef, inputsRef, engineRef, outputsRef]);

  return rail;
}

/**
 * Side rails — primary-green brackets connecting the middle of the engine panel
 * to the middle of the outputs panel on both sides, with a pulse travelling the
 * full line. Labelled "Self updating loop" (left) and "Practitioner review".
 */
function SideRails({
  rail,
  playing,
}: {
  rail: RailGeometry | null;
  playing: boolean;
}) {
  if (!rail) return null;

  const NUB = 20;
  const STROKE = 2.5;
  const leftRailX = rail.leftPanelX - NUB;
  const rightRailX = rail.rightPanelX + NUB;
  // Left rail (self-updating loop): inputs ↔ engine.
  const leftTopY = rail.inputsMidY;
  const leftBottomY = rail.engineMidY;
  const leftMidY = (leftTopY + leftBottomY) / 2;
  // Right rail (practitioner review): engine ↔ outputs.
  const rightTopY = rail.engineMidY;
  const rightBottomY = rail.outputsMidY;
  const rightMidY = (rightTopY + rightBottomY) / 2;

  const leftPath = `M ${rail.leftPanelX} ${leftTopY} H ${leftRailX} V ${leftBottomY} H ${rail.leftPanelX}`;
  const rightPath = `M ${rail.rightPanelX} ${rightTopY} H ${rightRailX} V ${rightBottomY} H ${rail.rightPanelX}`;

  return (
    <>
      <svg
        className="pointer-events-none absolute inset-0 z-10"
        width={rail.width}
        height={rail.height}
        fill="none"
      >
        <path
          d={leftPath}
          stroke="hsl(var(--primary))"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={rightPath}
          stroke="hsl(var(--primary))"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {playing && (
          <>
            <motion.circle
              cx={leftRailX}
              fill="hsl(var(--primary))"
              initial={{ cy: leftBottomY, r: 3.5 }}
              animate={{ cy: [leftBottomY, leftTopY], r: [3, 5, 3] }}
              transition={{
                cy: {
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatType: "reverse",
                },
                r: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
              }}
            />
            <motion.circle
              cx={rightRailX}
              fill="hsl(var(--primary))"
              initial={{ cy: rightBottomY, r: 3.5 }}
              animate={{ cy: [rightBottomY, rightTopY], r: [3, 5, 3] }}
              transition={{
                cy: {
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatType: "reverse",
                },
                r: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
              }}
            />
          </>
        )}
      </svg>

      {/* Labels — centred in the gutter, close to the rail line */}
      <span
        className="pointer-events-none absolute z-10 -translate-y-1/2 text-center text-[9px] leading-[1.2] text-foreground/70"
        style={{ left: 4, top: leftMidY, width: leftRailX - 8 }}
      >
        Self updating
        <br />
        loop
      </span>
      <span
        className="pointer-events-none absolute z-10 -translate-y-1/2 text-center text-[9px] leading-[1.2] text-foreground/70"
        style={{ right: 4, top: rightMidY, width: rail.width - rightRailX - 8 }}
      >
        Practitioner
        <br />
        review
      </span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Engine viz — one per capability
// ---------------------------------------------------------------------------

function EngineViz({ task, playing }: { task: number; playing: boolean }) {
  if (task === 0) return <AssetsLiabilitiesViz playing={playing} />;
  if (task === 1) return <CreditorListViz playing={playing} />;
  if (task === 2) return <DeadlinesViz playing={playing} />;
  if (task === 3) return <InsolvencyViz playing={playing} />;
  return <VoidablesViz playing={playing} />;
}

/** Light one-shot "pop in" for output vizzes (scale + fade). */
const POP_IN = { duration: 0.45, ease: "easeOut" as const };

/** Row entrance animation, mirroring the platform's rows dropping into a table. */
function rowMotion(i: number, playing: boolean) {
  return {
    initial: playing ? { opacity: 0, y: 5 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { delay: 0.1 + i * 0.13, duration: 0.3 },
  } as const;
}

/** A neutral placeholder "name" bar — stands in for redacted entity detail. */
function NameBar({ w }: { w: number }) {
  return (
    <div className="min-w-0 flex-1">
      <span
        className="block h-1.5 rounded-full bg-foreground/30"
        style={{ width: `${w}%` }}
      />
    </div>
  );
}

/** Stylised mini data-table shell — mirrors the platform's summary tables. */
function MiniTable({
  title,
  meta,
  footer,
  bodyClassName,
  children,
}: {
  title: string;
  meta?: ReactNode;
  footer?: ReactNode;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-md border border-primary/10">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-primary/10 bg-primary/[0.04] px-2 py-1">
        <span className="truncate text-[9.5px] font-semibold text-primary">
          {title}
        </span>
        {meta && (
          <span className="shrink-0 text-[8px] font-medium text-primary/70">
            {meta}
          </span>
        )}
      </div>
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col divide-y divide-primary/[0.06]",
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer && (
        <div className="shrink-0 border-t border-primary/10 bg-primary/[0.02] px-2 py-1 text-[8px] font-medium text-primary/70">
          {footer}
        </div>
      )}
    </div>
  );
}

/** A group sub-total header row (mirrors the grouped summary tables). */
function GroupRow({
  badge,
  chip,
  count,
  total,
  totalClass,
}: {
  badge: string;
  chip: string;
  count: string;
  total: string;
  totalClass?: string;
}) {
  return (
    <div className="flex items-center gap-1.5 bg-primary/[0.02] px-2 py-0.5">
      <span
        className={cn(
          "shrink-0 rounded px-1 py-px text-[7.5px] font-semibold",
          chip,
        )}
      >
        {badge}
      </span>
      <span className="text-[7.5px] text-foreground/55">{count}</span>
      <span
        className={cn(
          "ml-auto font-mono text-[8px] font-semibold tabular-nums text-foreground/75",
          totalClass,
        )}
      >
        {total}
      </span>
    </div>
  );
}

/** A single asset row inside {@link AssetsLiabilitiesViz}'s mini table. */
function AssetRow({
  i,
  w,
  value,
  playing,
}: {
  i: number;
  w: number;
  value: string;
  playing: boolean;
}) {
  return (
    <motion.div
      {...rowMotion(i, playing)}
      className="flex flex-1 items-center gap-1.5 px-2"
    >
      <NameBar w={w} />
      <span className="shrink-0 text-[8px] font-medium text-emerald-600">
        Identified
      </span>
      <span className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[7.5px] font-semibold text-amber-700">
        Secured
      </span>
      <span className="w-9 shrink-0 text-right font-mono text-[8.5px] font-semibold tabular-nums text-foreground/85">
        {value}
      </span>
    </motion.div>
  );
}

/** 1 · Extract assets & liabilities — mirrors the Asset Summary table. */
function AssetsLiabilitiesViz({ playing }: { playing: boolean }) {
  let i = 0;
  return (
    <MiniTable title="Asset summary" meta="Low $7.8k · High $62k">
      <motion.div {...rowMotion(i++, playing)}>
        <GroupRow
          badge="Stock / Inventory"
          chip="bg-amber-100 text-amber-700"
          count="1 asset"
          total="$30k"
        />
      </motion.div>
      <AssetRow i={i++} w={74} value="$30k" playing={playing} />
      <motion.div {...rowMotion(i++, playing)}>
        <GroupRow
          badge="Cash at Bank"
          chip="bg-emerald-100 text-emerald-700"
          count="4 assets"
          total="$31k"
        />
      </motion.div>
      <AssetRow i={i++} w={68} value="$22k" playing={playing} />
      <AssetRow i={i++} w={56} value="$6.7k" playing={playing} />
      <motion.div {...rowMotion(i++, playing)}>
        <GroupRow
          badge="Plant & Equipment"
          chip="bg-blue-100 text-blue-700"
          count="1 asset"
          total="$1.5k"
        />
      </motion.div>
      <AssetRow i={i} w={62} value="$1.5k" playing={playing} />
    </MiniTable>
  );
}

/** A single creditor row inside {@link CreditorListViz}'s mini table. */
function CreditorRow({
  i,
  playing,
  w,
  source,
  sourceChip,
  secured,
  securedChip,
  confidence,
  confidenceChip,
  amount,
}: {
  i: number;
  playing: boolean;
  w: number;
  source: string;
  sourceChip: string;
  secured: string;
  securedChip: string;
  confidence: string;
  confidenceChip: string;
  amount: string;
}) {
  return (
    <motion.div
      {...rowMotion(i, playing)}
      className="flex flex-1 items-center gap-1.5 px-2"
    >
      <NameBar w={w} />
      <span
        className={cn(
          "shrink-0 rounded px-1 py-0.5 text-[7.5px] font-medium",
          sourceChip,
        )}
      >
        {source}
      </span>
      <span
        className={cn(
          "shrink-0 rounded px-1 py-0.5 text-[7.5px] font-medium",
          securedChip,
        )}
      >
        {secured}
      </span>
      <span
        className={cn(
          "shrink-0 rounded px-1 py-0.5 text-[7.5px] font-medium",
          confidenceChip,
        )}
      >
        {confidence}
      </span>
      <span className="w-9 shrink-0 text-right font-mono text-[8.5px] font-semibold tabular-nums text-rose-600">
        {amount}
      </span>
    </motion.div>
  );
}

/** 2 · Generate creditor list & match POD — mirrors the Creditors table. */
function CreditorListViz({ playing }: { playing: boolean }) {
  let i = 0;
  const pod = "bg-blue-100 text-blue-700";
  const bank = "bg-sky-100 text-sky-700";
  const secured = "bg-amber-100 text-amber-700";
  const unsecured = "bg-gray-100 text-gray-500";
  const high = "bg-emerald-100 text-emerald-700";
  const medium = "bg-amber-100 text-amber-700";
  return (
    <MiniTable title="Creditors" meta="Total owed $950k">
      <motion.div {...rowMotion(i++, playing)}>
        <GroupRow
          badge="Other"
          chip="bg-gray-100 text-gray-600"
          count="3 creditors"
          total="$520k"
          totalClass="text-rose-600"
        />
      </motion.div>
      <CreditorRow
        i={i++}
        playing={playing}
        w={70}
        source="Proof of Debt"
        sourceChip={pod}
        secured="Unsecured"
        securedChip={unsecured}
        confidence="Medium"
        confidenceChip={medium}
        amount="$450k"
      />
      <CreditorRow
        i={i++}
        playing={playing}
        w={54}
        source="Proof of Debt"
        sourceChip={pod}
        secured="Unsecured"
        securedChip={unsecured}
        confidence="High"
        confidenceChip={high}
        amount="$46k"
      />
      <motion.div {...rowMotion(i++, playing)}>
        <GroupRow
          badge="Loan"
          chip="bg-emerald-100 text-emerald-700"
          count="3 creditors"
          total="$390k"
          totalClass="text-rose-600"
        />
      </motion.div>
      <CreditorRow
        i={i++}
        playing={playing}
        w={66}
        source="Bank statement"
        sourceChip={bank}
        secured="Secured"
        securedChip={secured}
        confidence="High"
        confidenceChip={high}
        amount="$364k"
      />
      <CreditorRow
        i={i}
        playing={playing}
        w={48}
        source="Proof of Debt"
        sourceChip={pod}
        secured="Unsecured"
        securedChip={unsecured}
        confidence="Medium"
        confidenceChip={medium}
        amount="$25k"
      />
    </MiniTable>
  );
}

/** 3 · Auto-calculate deadlines & review windows — mirrors Key Dates. */
function DeadlinesViz({ playing }: { playing: boolean }) {
  const rows = [
    {
      tone: "blue",
      icon: Flag,
      title: "Appointment date",
      category: "Appointment anchors",
      date: "27 Jun",
    },
    {
      tone: "blue",
      icon: Flag,
      title: "Commencement date",
      category: "Appointment anchors",
      date: "27 Jun",
    },
    {
      tone: "blue",
      icon: Flag,
      title: "Relation-back date (s91)",
      category: "Appointment anchors",
      date: "27 Jun",
    },
    {
      tone: "red",
      icon: FileText,
      title: "Notice of appointment (Form 505)",
      category: "Reporting & lodgements",
      date: "29 Jun",
    },
    {
      tone: "red",
      icon: FileText,
      title: "ATO + authority notification",
      category: "Reporting & lodgements",
      date: "29 Jun",
    },
    {
      tone: "red",
      icon: FileText,
      title: "ROCAP receipt due",
      category: "Reporting & lodgements",
      date: "3 Jul",
    },
  ] as const;
  const tones = {
    blue: {
      row: "bg-blue-50 dark:bg-blue-500/10",
      icon: "text-blue-500",
      title: "text-slate-700 dark:text-slate-200",
      chip: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    },
    red: {
      row: "bg-red-50 dark:bg-red-500/10",
      icon: "text-red-500",
      title: "text-slate-700 dark:text-slate-200",
      chip: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
    },
  };
  return (
    <MiniTable
      title="Key dates"
      meta="15 events"
      bodyClassName="gap-1 divide-y-0 p-1.5"
    >
      {rows.map((r, i) => {
        const t = tones[r.tone];
        const Icon = r.icon;
        return (
          <motion.div
            key={i}
            {...rowMotion(i, playing)}
            className={cn(
              "flex flex-1 items-center gap-1.5 rounded px-1.5",
              t.row,
            )}
          >
            <Icon className={cn("h-3 w-3 shrink-0", t.icon)} />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "truncate text-[9px] font-semibold leading-tight",
                  t.title,
                )}
              >
                {r.title}
              </p>
              <p className="truncate text-[7.5px] leading-tight text-foreground/50">
                {r.category}
              </p>
            </div>
            <span
              className={cn(
                "shrink-0 rounded px-1 py-0.5 text-[7.5px] font-semibold",
                t.chip,
              )}
            >
              {r.date}
            </span>
          </motion.div>
        );
      })}
    </MiniTable>
  );
}

/** 4 · Estimate date of insolvency — mirrors the Financial Analysis charts. */
function InsolvencyViz({ playing }: { playing: boolean }) {
  // Two periods, three series each (assets / liabilities / net) — heights are
  // illustrative only. Net dips negative in the later period.
  const periods = [
    [0.85, 0.45, 0.4],
    [0.95, 0.6, 0.25],
  ];
  const series = ["bg-emerald-500", "bg-rose-400", "bg-sky-500"];
  return (
    <MiniTable
      title="Financial analysis"
      meta={
        <span className="rounded bg-rose-100 px-1 py-0.5 font-semibold text-rose-700">
          Est. insolvency 30 Jun
        </span>
      }
      bodyClassName="divide-y-0 p-2"
    >
      {/* Metric rows */}
      <div className="mb-1.5 space-y-1">
        {[
          { label: "Total assets", v1: "($24k)", v2: "($24k)" },
          { label: "Total liabilities", v1: "($10k)", v2: "($10k)" },
          { label: "Net assets", v1: "($14k)", v2: "($14k)" },
        ].map((m, i) => (
          <motion.div
            key={m.label}
            {...rowMotion(i, playing)}
            className="flex items-center text-[8px]"
          >
            <span className="flex-1 text-foreground/55">{m.label}</span>
            <span className="w-12 text-right font-mono font-semibold tabular-nums text-foreground/65">
              {m.v1}
            </span>
            <span className="w-12 text-right font-mono font-semibold tabular-nums text-foreground/65">
              {m.v2}
            </span>
          </motion.div>
        ))}
      </div>
      <span className="text-[7.5px] font-semibold uppercase tracking-wider text-foreground/55">
        Net asset analysis
      </span>
      <div className="flex flex-1 items-stretch justify-around gap-4 pt-1">
        {periods.map((bars, p) => (
          <div key={p} className="flex flex-1 flex-col">
            <div className="flex flex-1 items-end justify-center gap-1.5">
              {bars.map((h, s) => (
                <motion.span
                  key={s}
                  className={cn("w-3 rounded-sm", series[s])}
                  style={{ height: `${h * 100}%`, originY: 1 }}
                  initial={playing ? { scaleY: 0 } : false}
                  animate={{ scaleY: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.2 + (p * 3 + s) * 0.08,
                    ease: "easeOut",
                  }}
                />
              ))}
            </div>
            <span className="mt-1 text-center text-[7px] font-medium text-foreground/55">
              {p === 0 ? "FY22" : "FY24"}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-1 flex items-center justify-center gap-2">
        {["Assets", "Liabilities", "Net"].map((label, s) => (
          <span
            key={label}
            className="flex items-center gap-1 text-[7px] text-foreground/60"
          >
            <span className={cn("h-1.5 w-1.5 rounded-sm", series[s])} />
            {label}
          </span>
        ))}
      </div>
    </MiniTable>
  );
}

/** 5 · Investigate voidables, civil & criminal claims — mirrors Voidables. */
function VoidablesViz({ playing }: { playing: boolean }) {
  const rows = [
    {
      w: 78,
      prov: "s588FA / s588FE",
      amount: "$270k",
      review: "45",
      conf: "82%",
    },
    { w: 62, prov: "s588G / s588M", amount: "$131k", review: "6", conf: "78%" },
    { w: 70, prov: "s588FDA", amount: "$104k", review: "9", conf: "78%" },
    { w: 56, prov: "s588FB", amount: "$97k", review: "9", conf: "85%" },
    { w: 66, prov: "s588FE(5)", amount: "$363k", review: "17", conf: "82%" },
  ];
  return (
    <MiniTable title="Recoverable transactions" meta="104 transactions · $617k">
      {rows.map((r, i) => (
        <motion.div
          key={i}
          {...rowMotion(i, playing)}
          className="flex flex-1 items-center gap-1.5 px-2"
        >
          <div className="min-w-0 flex-1">
            <NameBar w={r.w} />
            <span className="mt-0.5 block text-[7px] font-medium text-foreground/50">
              {r.prov}
            </span>
          </div>
          <span className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[7.5px] font-semibold text-amber-700">
            Transactions identified
          </span>
          <span className="w-9 shrink-0 text-right font-mono text-[8.5px] font-semibold tabular-nums text-rose-600">
            {r.amount}
          </span>
          <span className="w-5 shrink-0 text-right text-[8px] font-semibold tabular-nums text-foreground/70">
            {r.review}
          </span>
          <span className="shrink-0 rounded bg-emerald-100 px-1 py-0.5 text-[7.5px] font-semibold text-emerald-700">
            Live
          </span>
        </motion.div>
      ))}
    </MiniTable>
  );
}

// ---------------------------------------------------------------------------
// Output viz — continuous loops
// ---------------------------------------------------------------------------

/**
 * Day-one letter wireframe — reproduced 1:1 from Figma (node 16557:17615).
 * Rects: [x, y, w, h, fill]. Frame is 476 × 468.
 */
const DAY_ONE_RECTS: Array<[number, number, number, number, string]> = [
  // Letterhead: logo mark + wordmark
  [21, 30, 19, 17, "#d9d9d9"],
  [52, 30, 58, 17, "#d9d9d9"],
  // Contact block (right)
  [397, 35, 58, 7, "#d9d9d9"],
  [397, 48, 58, 7, "#d9d9d9"],
  // Recipient / company (bold)
  [21, 116, 173, 12, "#c1c1c1"],
  [21, 139, 70, 12, "#d9d9d9"],
  [21, 160, 119, 12, "#d9d9d9"],
  [21, 182, 158, 12, "#d9d9d9"],
  // Body
  [21, 226, 70, 12, "#d9d9d9"],
  [21, 248, 150, 12, "#c1c1c1"],
  [21, 270, 434, 12, "#d9d9d9"],
  [21, 292, 434, 12, "#d9d9d9"],
  [21, 313, 434, 12, "#d9d9d9"],
  [21, 335, 282, 12, "#d9d9d9"],
  // Sign-off + rule
  [21, 385, 35, 12, "#d9d9d9"],
  [21, 435, 70, 3, "#d9d9d9"],
];

function DayOnesViz({ playing }: { playing: boolean }) {
  const reveal = { duration: 2.6, ease: "easeInOut" as const };
  return (
    <div className="relative h-full w-full overflow-hidden rounded-md border border-primary/25 bg-white dark:bg-card">
      <svg
        viewBox="0 0 476 468"
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full p-1"
      >
        {DAY_ONE_RECTS.map(([x, y, w, h, fill], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} rx={2} fill={fill} />
        ))}
      </svg>

      {/* "being generated" reveal — plays once, then static */}
      <motion.div
        className="pointer-events-none absolute inset-0 origin-bottom bg-white dark:bg-card"
        initial={playing ? { scaleY: 1 } : { scaleY: 0 }}
        animate={{ scaleY: 0 }}
        transition={reveal}
      />
      <motion.div
        className="pointer-events-none absolute inset-x-1 h-[1.5px] rounded-full bg-primary/50"
        initial={playing ? { top: "1%", opacity: 1 } : { opacity: 0 }}
        animate={{ top: "99%", opacity: 0 }}
        transition={reveal}
      />
    </div>
  );
}

/**
 * Statutory report wireframe — reproduced 1:1 from Figma (node 16557:17637).
 * Frame is 476 × 468.
 */
const REPORT_RECTS: Array<[number, number, number, number, string]> = [
  // Header block (bold)
  [21, 65, 179, 26, "#c1c1c1"],
  // Body
  [21, 194, 158, 16, "#d9d9d9"],
  [21, 220, 136, 16, "#d9d9d9"],
  [21, 246, 126, 16, "#d9d9d9"],
  // Lower-left
  [21, 343, 49, 12, "#d9d9d9"],
  [21, 376, 87, 16, "#d9d9d9"],
  [21, 402, 110, 16, "#d9d9d9"],
  // Lower-right cluster
  [363, 350, 82, 10, "#d9d9d9"],
  [346, 366, 99, 10, "#d9d9d9"],
  [315, 384, 130, 10, "#d9d9d9"],
  [335, 402, 110, 10, "#d9d9d9"],
];

function ReportingViz({ playing }: { playing: boolean }) {
  const reveal = { duration: 2.6, ease: "easeInOut" as const };
  return (
    <div className="relative h-full w-full overflow-hidden rounded-md border border-primary/25 bg-white dark:bg-card">
      <svg
        viewBox="0 0 476 468"
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full p-1"
      >
        {REPORT_RECTS.map(([x, y, w, h, fill], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} rx={2} fill={fill} />
        ))}
        <text
          x={200.5}
          y={134}
          textAnchor="middle"
          className="fill-foreground font-bold"
          style={{ fontSize: 20 }}
        >
          STATUTORY REPORT TO CREDITORS
        </text>
      </svg>

      {/* "being generated" reveal — plays once, then static */}
      <motion.div
        className="pointer-events-none absolute inset-0 origin-bottom bg-white dark:bg-card"
        initial={playing ? { scaleY: 1 } : { scaleY: 0 }}
        animate={{ scaleY: 0 }}
        transition={reveal}
      />
      <motion.div
        className="pointer-events-none absolute inset-x-1 h-[1.5px] rounded-full bg-primary/50"
        initial={playing ? { top: "1%", opacity: 1 } : { opacity: 0 }}
        animate={{ top: "99%", opacity: 0 }}
        transition={reveal}
      />
    </div>
  );
}

function FormsViz({ playing }: { playing: boolean }) {
  const fields = [70, 90, 60, 80, 65];
  return (
    <motion.div
      className="flex h-full flex-col justify-center gap-2"
      initial={playing ? { opacity: 0, scale: 0.92 } : false}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={POP_IN}
    >
      {fields.map((w, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <motion.span
            className="flex h-3 w-3 shrink-0 items-center justify-center rounded-[3px] border border-primary/30"
            initial={false}
            animate={
              playing
                ? {
                    backgroundColor: [
                      "hsl(var(--primary) / 0)",
                      "hsl(var(--primary) / 0)",
                      "hsl(var(--primary) / 1)",
                      "hsl(var(--primary) / 1)",
                    ],
                  }
                : {}
            }
            transition={{
              duration: 2.6,
              times: [0, 0.4 + i * 0.1, 0.55 + i * 0.1, 1],
              repeat: Infinity,
              repeatDelay: 0.2,
            }}
          >
            <Check className="h-2 w-2 text-white" strokeWidth={3} />
          </motion.span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-sm bg-[#ECECEC]">
            <motion.div
              className="h-full rounded-sm bg-[#C1C1C1]"
              style={{ originX: 0 }}
              initial={false}
              animate={
                playing
                  ? { scaleX: [0, w / 100, w / 100, w / 100] }
                  : { scaleX: w / 100 }
              }
              transition={{
                duration: 2.6,
                times: [0, 0.25 + i * 0.1, 0.9, 1],
                repeat: Infinity,
                repeatDelay: 0.2,
                ease: "easeOut",
              }}
            />
          </div>
        </div>
      ))}
    </motion.div>
  );
}

/**
 * Creditor correspondence — a static mini email composer addressed to a
 * fictitious creditor (Lumi Finance). Rendered as a still card (no animation).
 */
const CORR_BODY_LINES = [100, 92, 96, 70, 48];

function CorrespondenceViz({ playing }: { playing: boolean }) {
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <motion.div
        className="relative w-full rounded-md border border-primary/15 bg-white shadow-sm dark:bg-card"
        initial={playing ? { opacity: 0, scale: 0.92 } : false}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={POP_IN}
      >
        {/* Header — recipient */}
        <div className="flex items-center gap-1 rounded-t-md border-b border-primary/10 bg-primary/[0.04] px-1.5 py-1">
          <span className="text-[7px] font-medium uppercase tracking-wide text-foreground/40">
            To
          </span>
          <span className="flex items-center gap-1 rounded-full bg-primary/10 px-1 py-px">
            <span className="flex h-2.5 w-2.5 items-center justify-center rounded-full bg-primary text-[5px] font-bold leading-none text-white">
              L
            </span>
            <span className="text-[7.5px] font-semibold leading-none text-primary">
              Lumi Finance
            </span>
          </span>
        </div>

        {/* Subject */}
        <div className="border-b border-primary/[0.06] px-1.5 py-1">
          <span className="block truncate text-[8px] font-semibold text-foreground/70">
            Notice of Appointment — Acme Pty Ltd
          </span>
        </div>

        {/* Body */}
        <div className="space-y-1 px-1.5 py-1.5">
          {CORR_BODY_LINES.map((w, i) => (
            <div
              key={i}
              className="h-1 rounded-full bg-primary/15"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>

        {/* Footer — send button */}
        <div className="flex items-center justify-end border-t border-primary/[0.06] px-1.5 py-1">
          <span className="flex items-center gap-1 rounded-full bg-primary px-1.5 py-0.5 text-[7.5px] font-semibold text-white shadow-sm">
            Send
            <Send className="h-2 w-2" />
          </span>
        </div>
      </motion.div>
    </div>
  );
}
